/** Original reusable 3D shophouse kit. Render only: geometry never feeds collision or simulation. */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { CreateBoxVertexData } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinderVertexData } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateTorusVertexData } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Material } from '@babylonjs/core/Materials/material';
import type { Scene } from '@babylonjs/core/scene';

type Rgb = readonly [number, number, number];
type Point = readonly [number, number, number];
export type AdventureSurface = 'plaster' | 'paving' | 'wood' | 'metal' | 'fabric' | 'leaf' | 'asphalt';
export interface AdventureBenchmarkWorldOptions {
  startZ?: number;
  length?: number;
  roadHalfWidth?: number;
  /** Caller owns materials returned by this factory. Names stay stable across runs. */
  material?: (name: string, rgb: Rgb, surface?: AdventureSurface) => Material;
}
export interface AdventureBenchmarkWorld {
  root: TransformNode;
  meshes: Mesh[];
  triangleCount: number;
  dispose(): void;
}
interface MaterialRef { material: Material; color: Rgb; }
interface Batch {
  material: Material;
  positions: number[];
  normals: number[];
  uvs: number[];
  indices: number[];
  colors: number[];
}
const PALETTE: readonly Rgb[] = [
  [.96,.82,.57], [.78,.81,.66], [.99,.91,.74],
  [.91,.71,.49], [.85,.85,.71], [.98,.80,.54],
];

/** An original leaf has a raised central vein and a tapered, curved tip. */
function leafData(): VertexData {
  const d=new VertexData(),positions:number[]=[],uvs:number[]=[],indices:number[]=[];
  const widths=[.006,.112,.145,.089,.004];
  for(let row=0;row<5;row++){
    const t=row/4,width=widths[row]!,bend=.11*Math.sin(t*Math.PI)+.20*t*t;
    positions.push(-width,t,bend+.025*Math.sin(t*Math.PI),0,t,bend,width,t,bend+.025*Math.sin(t*Math.PI));
    const imageV=.96-.94*t,uvHalf=.175*width/.145;
    uvs.push(.495-uvHalf,imageV,.495,imageV,.495+uvHalf,imageV);
    if(row<4)for(let side=0;side<2;side++){const k=row*3+side;indices.push(k,k+3,k+1,k+1,k+3,k+4);}
  }
  d.positions=positions;d.indices=indices;d.uvs=uvs;
  const normals:number[]=[];VertexData.ComputeNormals(positions,indices,normals);d.normals=normals;return d;
}
/** Flexible cloth strip: the sag and scalloping are geometry, visible from any angle. */
function awningData(): VertexData {
  const d=new VertexData(), positions:number[]=[], indices:number[]=[], uvs:number[]=[];
  for(let row=0;row<=6;row++) {
    const t=row/6;
    for(const x of [-.5,.5]) { positions.push(x,-.22*t-.065*t*t,t); uvs.push(x+.5,t); }
    if(row<6){const k=row*2;indices.push(k,k+2,k+1,k+1,k+2,k+3);}
  }
  const base=positions.length/3;
  for(let j=0;j<=6;j++) {
    const u=j/6;
    positions.push(u-.5,-.285,1,u-.5,-.405-.047*Math.sin(Math.PI*u),1.006);
    uvs.push(u,0,u,1);
    if(j<6){const k=base+j*2;indices.push(k,k+1,k+2,k+1,k+3,k+2);}
  }
  d.positions=positions;d.indices=indices;d.uvs=uvs;
  const normals:number[]=[];VertexData.ComputeNormals(positions,indices,normals);d.normals=normals;return d;
}

/** Static batched alley art. Ground-level furniture stays outside the configured road width. */
export function buildAdventureBenchmarkWorld(scene:Scene,options:AdventureBenchmarkWorldOptions={}):AdventureBenchmarkWorld {
  const startZ=options.startZ??-12,length=options.length??60;
  const edge=options.roadHalfWidth??3,facade=edge+1.13;
  const root=new TransformNode('adventureBenchmarkArchitecture',scene);
  const batches=new Map<string,Batch>(),materials=new Map<string,MaterialRef>(),surfaceMaterials=new Map<string,Material>();
  const ownMaterials:Material[]=[],ownTextures:DynamicTexture[]=[];
  const cube=CreateBoxVertexData({size:1});
  const cylinder=CreateCylinderVertexData({height:1,diameterTop:1,diameterBottom:1,tessellation:8});
  const potShape=CreateCylinderVertexData({height:1,diameterTop:1,diameterBottom:.68,tessellation:12});
  const leaf=leafData(),awning=awningData();
  const bicycleWheel=CreateTorusVertexData({diameter:.56,thickness:.033,tessellation:14});
  const chippedFace=new VertexData();
  chippedFace.positions=[-.5,-.5,0,.5,-.5,0,.34,.5,0,-.43,.14,0];chippedFace.normals=[0,0,1,0,0,1,0,0,1,0,0,1];chippedFace.uvs=[1,1,0,1,0,0,1,0];chippedFace.indices=[0,2,1,0,3,2];
  const signPlane=new VertexData();
  signPlane.positions=[-.5,-.5,0,.5,-.5,0,.5,.5,0,-.5,.5,0];
  signPlane.normals=[0,0,1,0,0,1,0,0,1,0,0,1];
  signPlane.uvs=[1,1,0,1,0,0,1,0];signPlane.indices=[0,2,1,0,3,2];

  function material(name:string,rgb:Rgb,surface?:AdventureSurface):MaterialRef {
    const previous=materials.get(name);if(previous)return previous;
    const surfaceKey=name.startsWith('facade-')||name==='warm-shop-lamp'?name:surface??'plain';
    let m=surfaceMaterials.get(surfaceKey);
    if(!m){
      if(options.material)m=options.material('surface-'+surfaceKey,[1,1,1],surface);
      else{
        const pbr=new PBRMaterial('adventure-surface-'+surfaceKey,scene);
        pbr.albedoColor=Color3.White();pbr.metallic=surface==='metal'?.3:0;
        pbr.roughness=surface==='metal'?.66:.94;m=pbr;ownMaterials.push(m);
      }
      if(surface==='leaf'||surface==='fabric')m.backFaceCulling=false;
      if(surface==='leaf'&&m instanceof PBRMaterial){m.twoSidedLighting=true;m.roughness=.94;m.environmentIntensity=.28;}
      if(surface==='paving'&&m instanceof PBRMaterial){
        const stone=surfaceMaterials.get('plaster');
        if(stone instanceof PBRMaterial){m.albedoTexture=stone.albedoTexture;m.bumpTexture=stone.bumpTexture;m.invertNormalMapY=stone.invertNormalMapY;}
      }
      surfaceMaterials.set(surfaceKey,m);
    }
    const tint:Rgb=surface==='leaf'&&m instanceof PBRMaterial&&m.albedoTexture?(name==='deep-leaf'?[.72,.83,.64]:name==='sun-leaf'?[1,1,.88]:[.88,.96,.78]):rgb;
    const linear=Color3.FromArray([...tint]).toLinearSpace();
    const ref:MaterialRef={material:m,color:[linear.r,linear.g,linear.b]};materials.set(name,ref);return ref;
  }
  const dark=material('shop-interior',[.105,.098,.076]);
  const trim=material('aged-limestone',[.60,.57,.47],'plaster');
  const paleTrim=material('pale-cornice',[.79,.76,.65],'plaster');
  const brick=material('exposed-brick',[.43,.25,.17],'plaster');
  const wood=material('dark-teak',[.25,.16,.088],'wood');
  const rail=material('weathered-painted-iron',[.15,.19,.165],'metal');
  const shutter=material('faded-green-shutters',[.22,.30,.25],'wood');
  const shutterBlue=material('faded-blue-shutters',[.20,.29,.30],'wood');
  const soil=material('soil-rubber-cable',[.067,.059,.043]);
  const pot=material('terracotta',[.40,.235,.125],'plaster');
  const pottery=material('old-ceramic',[.52,.53,.37],'plaster');
  const steel=material('dull-galvanised-steel',[.40,.43,.40],'metal');
  const pavement=material('sidewalk-pavers',[.22,.205,.17],'paving');
  const curb=material('worn-curbstone',[.255,.235,.20],'paving');
  const freshStone=material('exposed-step-chip',[.45,.425,.36],'paving');
  const paverTints=[material('paver-warm',[.23,.21,.17],'paving'),material('paver-grey',[.21,.21,.185],'paving'),material('paver-old',[.18,.175,.15],'paving'),material('paver-sand',[.265,.235,.19],'paving')];
  const leaves=[
    material('deep-leaf',[.105,.19,.065],'leaf'),
    material('green-leaf',[.22,.30,.095],'leaf'),
    material('sun-leaf',[.36,.41,.16],'leaf'),
  ];
  const clothCream=material('awning-natural-canvas',[.75,.69,.51],'fabric');
  const clothOrange=material('awning-terracotta-canvas',[.57,.22,.10],'fabric');
  const clothGreen=material('awning-green-canvas',[.12,.255,.23],'fabric');
  const flagRed=material('faded-vietnam-flag',[.61,.075,.043],'fabric');
  const flagYellow=material('flag-gold',[.97,.66,.08],'fabric');
  const plaster=PALETTE.map((rgb,i)=>material('ochre-plaster-'+i,rgb,'plaster'));
  const shopWalls=[material('facade-noodle',[.94,.78,.50],'plaster'),material('facade-coffee',[.63,.75,.61],'plaster'),material('facade-grocery',[.90,.85,.68],'plaster'),material('facade-home',[.86,.69,.56],'plaster')];
  const roofTile=material('aged-roof-tile',[.46,.22,.105],'plaster');
  const glass=material('old-window-glass',[.11,.19,.18],'metal');
  const shopLamp=material('warm-shop-lamp',[1,.73,.38]);
  if(shopLamp.material instanceof PBRMaterial){shopLamp.material.emissiveColor.set(.86,.44,.12);shopLamp.material.roughness=1;}
  const identity=Quaternion.Identity();

  // Spatial chunks retain useful frustum culling without one draw call per prop.
  function add(data:VertexData,mat:MaterialRef,scale:Point,position:Point,rotation=identity):void {
    const chunk=Math.min(3,Math.floor((position[2]-startZ)/20)),key=mat.material.uniqueId+'-'+chunk;
    let b=batches.get(key);
    if(!b){b={material:mat.material,positions:[],normals:[],uvs:[],indices:[],colors:[]};batches.set(key,b);}
    const transform=Matrix.Compose(Vector3.FromArray([...scale]),rotation,Vector3.FromArray([...position]));
    const normalMatrix=Matrix.Transpose(Matrix.Invert(transform));
    const count=b.positions.length/3,p=data.positions!,n=data.normals!,uv=data.uvs!;
    for(let i=0;i<p.length;i+=3){
      const q=Vector3.TransformCoordinates(new Vector3(p[i]!,p[i+1]!,p[i+2]!),transform);
      const normal=Vector3.TransformNormal(new Vector3(n[i]!,n[i+1]!,n[i+2]!),normalMatrix).normalize();
      b.positions.push(q.x,q.y,q.z);b.normals.push(normal.x,normal.y,normal.z);b.colors.push(...mat.color,1);
    }
    // One texture repeat per metre on solid surfaces, independent of bay dimensions.
    // Tiny strips retain their material's original grain instead of stretching a full wall image.
    if(data===cube){
      for(let vertex=0;vertex<uv.length/2;vertex++){
        const normalIndex=vertex*3;
        const vertical=Math.abs(n[normalIndex+1]!)>.5;
        const alongX=Math.abs(n[normalIndex]!)>.5;
        const uScale=alongX?scale[2]:scale[0];
        const vScale=vertical?scale[2]:scale[1];
        b.uvs.push(uv[vertex*2]!*uScale,uv[vertex*2+1]!*vScale);
      }
    }else b.uvs.push(...Array.from(uv));
    for(const index of data.indices!)b.indices.push(count+index);
  }
  function box(mat:MaterialRef,size:Point,at:Point,rotation=identity):void{add(cube,mat,size,at,rotation);}
  function rod(mat:MaterialRef,from:Point,to:Point,radius:number):void{
    const a=Vector3.FromArray([...from]),b=Vector3.FromArray([...to]),delta=b.subtract(a);
    const dir=delta.normalizeToNew(),axis=Vector3.Cross(Vector3.Up(),dir);
    const angle=Math.acos(Math.max(-1,Math.min(1,Vector3.Dot(Vector3.Up(),dir))));
    const rot=axis.lengthSquared()<1e-8?Quaternion.RotationAxis(Vector3.Right(),angle):Quaternion.RotationAxis(axis.normalize(),angle);
    const middle=a.add(b).scale(.5);
    add(cube,mat,[radius*2,delta.length(),radius*2],[middle.x,middle.y,middle.z],rot);
  }

  /** Original broad wear masks. These are local surface marks, never collision geometry. */
  function wearMaterial(kind:'soil'|'runoff'|'repair'):MaterialRef{
    const texture=new DynamicTexture('adventure-wear-mask-'+kind,{width:512,height:512},scene,true);
    const ctx=texture.getContext() as unknown as CanvasRenderingContext2D;
    ctx.clearRect(0,0,512,512);
    const noise=(i:number)=>((Math.sin(i*12.9898+kind.length*78.233)*43758.5453)%1+1)%1;
    if(kind==='runoff'){
      // Irregular mineral streaks accumulate at the foot of a downpipe and a wall base.
      for(let i=0;i<48;i++){
        const x=110+noise(i)*290,y=75+noise(i+70)*350,w=4+noise(i+91)*31,h=60+noise(i+33)*230;
        const gradient=ctx.createLinearGradient(0,y,0,Math.min(504,y+h));
        gradient.addColorStop(0,'rgba(58,44,27,0)');gradient.addColorStop(.35,'rgba(58,44,27,.18)');gradient.addColorStop(1,'rgba(58,44,27,0)');
        ctx.fillStyle=gradient;ctx.fillRect(x,y,w,h);
      }
      const foot=ctx.createRadialGradient(256,455,15,256,455,245);
      foot.addColorStop(0,'rgba(48,43,26,.36)');foot.addColorStop(1,'rgba(48,43,26,0)');
      ctx.fillStyle=foot;ctx.fillRect(0,220,512,292);
    }else if(kind==='repair'){
      const outline=[[.13,.19],[.24,.11],[.68,.13],[.88,.20],[.85,.51],[.91,.72],[.76,.87],[.28,.84],[.11,.66],[.15,.46]];
      ctx.beginPath();outline.forEach((p,i)=>{if(i===0)ctx.moveTo(p[0]!*512,p[1]!*512);else ctx.lineTo(p[0]!*512,p[1]!*512);});ctx.closePath();
      ctx.fillStyle='rgba(39,36,30,.39)';ctx.fill();ctx.strokeStyle='rgba(33,30,26,.24)';ctx.lineWidth=3;ctx.stroke();
      ctx.save();ctx.clip();
      for(let i=0;i<1500;i++){ctx.fillStyle=i%3===0?'rgba(108,100,79,.08)':'rgba(18,16,13,.045)';ctx.fillRect(noise(i+30)*512,noise(i+200)*512,1+noise(i+90)*3,1+noise(i+110)*3);}
      ctx.restore();
    }else{
      for(let i=0;i<28;i++){
        const angle=noise(i+9)*Math.PI*2,r=noise(i+19)*100;
        const x=256+Math.cos(angle)*r,y=256+Math.sin(angle)*r,radius=30+noise(i+29)*90;
        const gradient=ctx.createRadialGradient(x,y,0,x,y,radius);
        const rgb='43,35,22';
        gradient.addColorStop(0,'rgba('+rgb+','+.095+')');
        gradient.addColorStop(1,'rgba('+rgb+',0)');
        ctx.fillStyle=gradient;ctx.fillRect(x-radius,y-radius,radius*2,radius*2);
      }
      // Fine discontinuities prevent a perfectly smooth airbrushed outline.
      ctx.globalCompositeOperation='destination-out';
      for(let i=0;i<90;i++){ctx.fillStyle='rgba(0,0,0,.10)';ctx.fillRect(noise(i+50)*512,noise(i+170)*512,1+noise(i+370)*5,2+noise(i+230)*7);}
      ctx.globalCompositeOperation='source-over';
    }
    texture.hasAlpha=true;texture.update(false);texture.anisotropicFilteringLevel=4;
    const m=new PBRMaterial('adventure-wear-'+kind,scene);
    m.albedoColor=Color3.White();m.albedoTexture=texture;m.useAlphaFromAlbedoTexture=true;
    m.transparencyMode=PBRMaterial.PBRMATERIAL_ALPHABLEND;m.backFaceCulling=false;m.roughness=1;m.metallic=0;
    m.environmentIntensity=.75;m.zOffset=-1;
    ownMaterials.push(m);ownTextures.push(texture);
    return {material:m,color:[1,1,1]};
  }
  const contactDust=wearMaterial('soil'),wallRunoff=wearMaterial('runoff'),roadRepair=wearMaterial('repair');
  const groundWearRotation=Quaternion.RotationYawPitchRoll(0,-Math.PI/2,0);
  function contactMark(x:number,z:number,w:number,d:number,y:number,angle=0):void{
    add(signPlane,contactDust,[w,d,1],[x,y,z],Quaternion.RotationYawPitchRoll(angle,0,0).multiply(groundWearRotation));
  }

  const signCache=new Map<string,MaterialRef>();
  function signMaterial(lines:string[],bg:string,ink:string,format:'fascia'|'board'='fascia'):MaterialRef{
    const key=lines.join('|')+'-'+format,found=signCache.get(key);if(found)return found;
    const width=format==='fascia'?1024:512,height=format==='fascia'?224:768;
    const texture=new DynamicTexture('adventure-sign-'+key,{width,height},scene,false);
    const ctx=texture.getContext() as unknown as CanvasRenderingContext2D;
    ctx.fillStyle=bg;ctx.fillRect(0,0,width,height);ctx.strokeStyle=ink;ctx.lineWidth=3;ctx.strokeRect(12,12,width-24,height-24);
    // Layout is authored for each physical board's aspect ratio, keeping letter proportions.
    ctx.globalAlpha=.11;
    for(let i=0;i<650;i++){const x=(i*167)%width,y=(i*251)%height;ctx.fillStyle=i%2?'#261b0b':'#ffecd3';ctx.fillRect(x,y,2+(i%17),1+(i%5));}
    ctx.globalAlpha=1;ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillStyle=ink;
    if(format==='fascia'){
      ctx.font='700 103px Georgia, serif';ctx.fillText(lines[0]!,width/2,82,width-60);
      ctx.font='600 53px Georgia, serif';ctx.fillText(lines[1]??'',width/2,163,width-70);
    }else{
      const h=(height-100)/lines.length;
      lines.forEach((line,index)=>{ctx.font='700 '+Math.min(87,h*.45)+'px Georgia, serif';ctx.fillText(line,width/2,50+h*(index+.5),width-46);});
    }
    texture.update(false);texture.anisotropicFilteringLevel=4;
    const mat=new PBRMaterial('adventure-painted-sign-'+signCache.size,scene);
    mat.albedoTexture=texture;mat.metallic=0;mat.roughness=.96;mat.backFaceCulling=false;
    const ref:MaterialRef={material:mat,color:[1,1,1]};signCache.set(key,ref);ownMaterials.push(mat);ownTextures.push(texture);return ref;
  }
  const signDefinitions=[
    {lines:['PHỞ GIA TRUYỀN','BÒ • GÀ'],bg:'#c0a365',ink:'#352c1b'},
    {lines:['CÀ PHÊ','VỈA HÈ'],bg:'#c4ac72',ink:'#302c22'},
    {lines:['BÁNH MÌ NÓNG','15K'],bg:'#ccb880',ink:'#6a2b19'},
    {lines:['TẠP HÓA','MINH AN'],bg:'#60776d',ink:'#ded2ad'},
    {lines:['BÚN CHẢ','HÀ NỘI'],bg:'#af9d6d',ink:'#302d22'},
    {lines:['TRÀ ĐÁ','NƯỚC MÍA'],bg:'#a4ad84',ink:'#314137'},
  ];
  const signs=signDefinitions.map(s=>signMaterial(s.lines,s.bg,s.ink));
  function boardSign(index:number):MaterialRef{const s=signDefinitions[index%signDefinitions.length]!;return signMaterial(s.lines,s.bg,s.ink,'board');}
  function plant(x:number,z:number,seed:number,size=1,y=.16):void{
    const height=.34*size,radius=.29*size;
    contactMark(x+.02*size,z,.94*size,.83*size,y+.007,seed*.37);
    add(potShape,seed%3===0?pottery:pot,[radius*2,height,radius*2],[x,y+height/2,z]);
    add(cylinder,pot,[radius*2.09,.045*size,radius*2.09],[x,y+height-.023*size,z]);
    add(cylinder,soil,[radius*1.75,.012,radius*1.75],[x,y+height+.003,z]);
    const base=y+height,trunk=.46*size;
    rod(wood,[x,base,z],[x+.025,base+trunk,z],.013*size);
    const branchCount=seed%3===0?6:5;
    for(let branch=0;branch<branchCount;branch++){
      const angle=branch*2.399+seed*.79,reach=(.19+(branch%3)*.05)*size;
      const topY=base+(.47+(branch%3)*.16)*size;
      const tip:Point=[x+Math.cos(angle)*reach,topY,z+Math.sin(angle)*reach];
      rod(wood,[x,base+.045*size,z],tip,.0065*size);
      for(let j=0;j<5;j++){
        const t=.18+j*.175,a=angle+(j%2===0?1.06:-1.06);
        const at:Point=[x+(tip[0]-x)*t,base+(tip[1]-base)*t,z+(tip[2]-z)*t];
        const dir=new Vector3(Math.cos(a)*.75,j===3?.56:.13+(j%3)*.13,Math.sin(a)*.75).normalize();
        const axis=Vector3.Cross(Vector3.Up(),dir).normalize(),orient=Quaternion.RotationAxis(axis,Math.acos(dir.y));
        const dimension=(.31+(j%3)*.065)*size;
        add(leaf,leaves[(seed+branch+j)%3]!,[dimension*(seed%3===0?1.42:1),dimension,dimension],at,orient);
      }
    }
  }

  /** Four authored shophouse families share construction primitives, not the same facade. */
  function house(side:number,center:number,width:number,index:number,kind:0|1|2|3,setback:number):void{
    const yaw=-side*Math.PI/2,rotation=Quaternion.RotationYawPitchRoll(yaw,0,0);
    const faceX=facade+setback,wall=shopWalls[kind]!,garage=side<0&&index===8;
    const point=(u:number,y:number,v:number):Point=>[side*(faceX-v),y,center+side*u];
    const place=(mat:MaterialRef,size:Point,u:number,y:number,v:number,rot=identity)=>box(mat,size,point(u,y,v),rotation.multiply(rot));
    const member=(mat:MaterialRef,a:Point,b:Point,r:number)=>rod(mat,point(...a),point(...b),r);
    const round=(mat:MaterialRef,size:Point,u:number,y:number,v:number)=>add(cylinder,mat,size,point(u,y,v));
    const floorCount=kind===0?(index%3===0?1:2):kind===2?3:2;
    const floorHeight=kind===1?2.90:kind===2?2.64:2.73;
    const groundHeight=kind===1?3.18:kind===0?2.95:3.02;
    const height=groundHeight+floorHeight*floorCount;
    const frontSetback=(floor:number)=>kind===2&&floor>0?-.20:kind===3&&floor===1?-.12:0;
    // A deep rear volume and party walls enclose the building. The street-facing wall is
    // built around real openings below; there is no solid facade behind a painted window.
    place(wall,[width-.05,height,1.1],0,height/2,-2.50);
    for(const u of [-width/2+.105,width/2-.105])place(wall,[.21,garage?height-groundHeight:height,2.32],u,garage?(height+groundHeight)/2:height/2,-1.13);
    place(dark,[width-.36,groundHeight-.15,.045],0,groundHeight/2,-1.72);
    place(trim,[width-.34,garage?.08:.13,1.83],0,garage?.12:.18,-.65);
    place(wood,[width-.25,.13,1.90],0,groundHeight-.05,-.77);
    if(!garage)for(const u of [-width/2+.14,width/2-.14]){
      place(kind===1?paleTrim:wall,[.25,groundHeight,1.75],u,groundHeight/2,-.70);
      place(trim,[.29,.36,.24],u,.34,.07);
    }
    const sign=(number:number,u:number,y:number,w:number,v=.18)=>{
      place(wood,[w,.46,.065],u,y,v);
      add(signPlane,signs[number]!,[w-.035,.435,1],point(u,y,v+.039),rotation);
    };
    function stool(u:number,v:number,y=.43):void{
      place(wood,[.32,.047,.31],u,y,v);
      for(const du of [-.11,.11])for(const dv of [-.11,.11])place(wood,[.038,y-.18,.038],u+du,(y+.18)/2,v+dv);
      place(wood,[.26,.035,.034],u,.24,v-.10);
      place(wood,[.26,.035,.034],u,.24,v+.10);
    }
    function cafeTable(u:number,v:number):void{
      round(wood,[.58,.052,.58],u,.74,v);
      member(rail,[u,.19,v],[u,.715,v],.023);
      for(const du of [-.20,.20])for(const dv of [-.16,.16])member(rail,[u,.35,v],[u+du,.18,v+dv],.015);
      round(pottery,[.078,.09,.078],u-.13,.81,v);
      round(steel,[.125,.14,.125],u+.11,.835,v+.045);
      stool(u-.47,v-.035);stool(u+.46,v+.04);
    }
    function crate(u:number,y:number,v:number,w=.52):void{
      place(wood,[w,.045,.43],u,y-.17,v);
      for(const du of [-w/2+.022,w/2-.022])for(const dv of [-.195,.195])place(wood,[.037,.36,.037],u+du,y,v+dv);
      for(const dy of [-.13,0,.13]){
        place(wood,[w,.09,.03],u,y+dy,v-.21);place(wood,[w,.09,.03],u,y+dy,v+.21);
        place(wood,[.03,.09,.39],u-w/2,y+dy,v);place(wood,[.03,.09,.39],u+w/2,y+dy,v);
      }
    }
    function canopy(u:number,w:number,y:number,depth:number,striped:boolean,color:MaterialRef):void{
      const stripes=striped?10:5;
      for(let stripe=0;stripe<stripes;stripe++)add(awning,striped&&stripe%2===0?clothCream:color,[w/stripes,1,depth],point(u-w/2+(stripe+.5)*w/stripes,y,.12),rotation);
      for(const du of [-w*.44,w*.44])member(rail,[u+du,y-.35,.12+depth],[u+du,y-.14,.025],.014);
    }
    function kitchenShelf(u:number,y:number,v:number,w:number):void{
      place(wood,[w,.07,.28],u,y,v);
      for(let j=0;j<5;j++){const h=.12+(j%3)*.045;round(j%2?pottery:pot,[.115,h,.115],u-w*.38+j*w*.19,y+h/2,v+.01);}
    }
    // Family 1: open pho kitchen, low tiled frontage, steel stockpots and a red canvas.
    function noodleShop():void{
      place(wood,[width*.53,.83,.62],-width*.18,.61,-.37);
      place(trim,[width*.55,.085,.71],-width*.18,1.07,-.31);
      for(const du of [-.42,.18]){
        round(steel,[.41,.38,.41],du-width*.13,1.30,-.29);
        round(rail,[.44,.036,.44],du-width*.13,1.495,-.29);
        member(steel,[du-width*.13-.24,1.38,-.29],[du-width*.13+.24,1.38,-.29],.018);
      }
      kitchenShelf(-width*.08,1.41,-1.46,width*.68);
      kitchenShelf(-width*.08,1.94,-1.46,width*.68);
      for(let j=0;j<6;j++)round(pottery,[.17,.025,.17],-width*.36,1.13+j*.025,-.10);
      sign(0,-width*.07,2.41,width*.82);
      canopy(-width*.025,width+.16,groundHeight+.17,.99,true,clothOrange);
      const u=width*.24,v=.46;stool(u,v);stool(u+.45,v-.07);
      const p=point(-width*.40,0,.34);plant(p[0],p[2],index,.90);
      place(rail,[.63,.11,.26],-.25,2.73,-.59);
      place(shopLamp,[.52,.025,.11],-.25,2.664,-.57);
    }
    // Family 2: coffee house with glazed timber joinery, a narrow entrance and round tables.
    function coffeeShop():void{
      const windowU=-width*.20,windowW=width*.43;
      place(wall,[windowW,.83,.16],windowU,.58,-.01);
      place(glass,[windowW,1.43,.024],windowU,1.71,-.065);
      for(const du of [-windowW/2,0,windowW/2])place(wood,[.065,1.57,.10],windowU+du,1.73,.011);
      for(const y of [1.02,1.75,2.48])place(wood,[windowW+.07,.060,.10],windowU,y,.02);
      const doorU=width*.31;
      for(const du of [-.52,.52])place(wood,[.075,2.45,.19],doorU+du,1.41,-.045);
      place(wood,[1.11,.10,.19],doorU,2.69,-.045);
      place(wood,[.72,2.29,.065],doorU+.24,1.36,-.42,Quaternion.RotationYawPitchRoll(-.72,0,0));
      place(trim,[1.13,.11,.62],doorU,.21,.17);
      sign(1,windowU,2.70,Math.min(width*.54,2.65),.11);
      canopy(windowU,width*.60,3.23,.82,false,clothGreen);
      cafeTable(-width*.16,.48);
      const p=point(width*.43,0,.26);plant(p[0],p[2],index,1.08);
      round(rail,[.22,.15,.22],doorU,2.56,-.46);
      round(shopLamp,[.10,.11,.10],doorU,2.46,-.46);
    }
    // Family 3: grocery with an asymmetrical rolling shutter, crates and stocked shelves.
    function groceryShop():void{
      const closedU=width*.31,closedW=width*.30;
      place(rail,[closedW,2.40,.09],closedU,1.45,-.015);
      for(let j=0;j<26;j++)place(steel,[closedW,.027,.055],closedU,.29+j*.087,.049);
      place(trim,[.16,2.90,.25],width*.10,1.45,.01);
      for(const y of [.78,1.24,1.70,2.16])kitchenShelf(-width*.25,y,-1.38,width*.52);
      crate(-width*.30,.39,.28);crate(-width*.30,.75,.27);crate(-width*.03,.39,.32,.41);
      for(let j=0;j<4;j++)round(pottery,[.14,.24,.14],-width*.43+j*.18,.70,.28);
      sign(3,-width*.20,2.51,width*.58);
      canopy(-width*.22,width*.57,3.10,.91,false,index%2?clothCream:clothGreen);
      place(steel,[.18,.35,.10],width*.43,1.71,.11);
      const p=point(width*.44,0,.29);plant(p[0],p[2],index,.73);
    }
    // Family 4: residential entry, stepped stoop, raised garden and solid panelled doors.
    function homeEntrance():void{
      const doorU=-width*.18;
      place(wall,[width*.22,2.92,.21],width*.37,1.46,-.015);
      place(wood,[1.13,2.30,.10],doorU,1.45,-.14);
      for(const du of [-.29,.29])for(const y of [.81,1.76])place(shutter,[.46,.72,.055],doorU+du,y,-.072);
      place(wood,[.055,2.31,.085],doorU,1.45,-.033);
      for(const du of [-.63,.63])place(paleTrim,[.13,2.49,.23],doorU+du,1.49,-.017);
      place(paleTrim,[1.39,.16,.26],doorU,2.76,.025);
      for(let step=0;step<3;step++){
        const sy=.20+step*.077,sv=.63-step*.21;place(trim,[1.46,.08,.24],doorU,sy,sv);
        for(let chip=0;chip<3;chip++)add(chippedFace,freshStone,[.047+(chip%2)*.037,.025+(step%2)*.012,1],point(doorU-.55+chip*.37+step*.04,sy+.021,sv+.122),rotation);
      }
      place(rail,[.028,.16,.035],doorU+.12,1.43,.027);
      const gardenU=width*.31;
      place(trim,[width*.31,.40,.58],gardenU,.38,.31);
      const p=point(gardenU,0,.31);plant(p[0],p[2],index,1.07,.59);
      const p2=point(gardenU-.32,0,.21);plant(p2[0],p2[2],index+3,.73,.59);
      place(wood,[1.48,.09,.78],doorU,2.97,.23,Quaternion.RotationYawPitchRoll(.0,.13,0));
      for(let j=0;j<7;j++)place(roofTile,[.20,.066,.83],doorU-.62+j*.205,3.035,.23,Quaternion.RotationYawPitchRoll(0,.13,0));
      place(steel,[.17,.23,.035],doorU+.84,1.83,.135);
    }
    if(garage){
      // This bay reserves a 4.5m parallel parking recess outside the configured road.
      for(let j=0;j<8;j++)place(steel,[width-.10,.036,.09],0,groundHeight-.13-j*.052,.012);
      place(rail,[width-.04,.10,.19],0,groundHeight+.012,.04);
      contactMark(point(0,0,.18)[0],center,1.52,4.30,.167,.13);
    }else [noodleShop,coffeeShop,groceryShop,homeEntrance][kind]!();

    function upperWindow(u:number,y:number,w:number,h:number,v:number,open:boolean,arched:boolean):void{
      // The glass/recess is half a metre behind the wall, with its sill and reveal faces visible.
      place(dark,[w,h,.025],u,y+h/2,v-.51);
      if(kind===1||kind===2)place(glass,[w-.08,h-.06,.027],u,y+h/2,v-.29);
      for(const du of [-w/2-.055,w/2+.055])place(kind===2?trim:paleTrim,[.105,h+.15,.29],u+du,y+h/2,v-.05);
      place(paleTrim,[w+.30,.11,.36],u,y-.035,v+.005);
      if(arched){
        const radius=w/2+.05,cy=y+h-radius;
        for(let segment=0;segment<10;segment++){
          const a=segment*Math.PI/10,b=(segment+1)*Math.PI/10;
          member(paleTrim,[u+Math.cos(a)*radius,cy+Math.sin(a)*radius,v+.06],[u+Math.cos(b)*radius,cy+Math.sin(b)*radius,v+.06],.055);
        }
        place(wood,[.055,h-.11,.075],u,y+h/2,v-.22);
        place(wood,[w-.06,.049,.08],u,y+h*.55,v-.22);
      }else place(kind===2?trim:paleTrim,[w+.25,.105,.29],u,y+h+.025,v-.025);
      if(kind===2){
        for(const du of [-w*.26,w*.26])place(steel,[.029,h-.06,.04],u+du,y+h/2,v-.25);
        place(steel,[w-.06,.031,.04],u,y+h*.54,v-.25);
      }else{
        const panelW=(w-.025)/2;
        for(const sign of [-1,1]){
          const pu=open?u+sign*(w/2+.12):u+sign*panelW/2;
          const pv=open?v+.12:v-.045,hinge=Quaternion.RotationYawPitchRoll(open?sign*.91:0,0,0);
          for(const du of [-panelW/2+.022,panelW/2-.022])place(shutter,[.044,h-.06,.075],pu+du,y+h/2,pv,hinge);
          if(kind===3){
            for(const yy of [y+h*.28,y+h*.73])place(shutter,[panelW-.06,h*.38,.064],pu,yy,pv,hinge);
          }else for(let row=0;row<9;row++)place(shutter,[panelW-.055,(h-.08)/10,.054],pu,y+.09+row*(h-.16)/9,pv,hinge.multiply(Quaternion.RotationYawPitchRoll(0,-.25,0)));
        }
      }
    }
    function balcony(u:number,y:number,w:number,depth:number,style:number,v:number):void{
      place(trim,[w,.13,depth],u,y,depth/2+v);
      place(style===2?wood:rail,[w-.06,.050,.045],u,y+.83,depth+v);
      place(style===2?wood:rail,[w-.06,.035,.032],u,y+.20,depth+v);
      const count=Math.max(5,Math.ceil(w/(style===1?.32:.26)));
      for(let j=0;j<count;j++){
        const uu=u-w/2+.065+j*(w-.13)/(count-1);
        member(style===2?wood:rail,[uu,y+.08,depth+v],[uu,y+.82,depth+v],style===2?.024:.011);
        if(style===1&&j%2===0)for(let k=0;k<5;k++){
          const a=k/5*Math.PI*2,b=(k+1)/5*Math.PI*2;
          member(rail,[uu+Math.sin(a)*.080,y+.48+Math.cos(a)*.135,depth+v],[uu+Math.sin(b)*.080,y+.48+Math.cos(b)*.135,depth+v],.007);
        }
      }
      for(const du of [-w/2+.025,w/2-.025])member(rail,[u+du,y+.83,v],[u+du,y+.83,depth+v],.024);
      if(style===0||style===2){const p=point(u+w*.28,0,v+depth*.56);plant(p[0],p[2],index+style,.69,y+.07);}
      if(style===0){
        for(let vine=0;vine<3;vine++){
          const vu=u-w*.22+vine*.20,vv=v+depth+.025;
          let previous:Point=[vu,y+.30,vv];
          for(let j=0;j<6;j++){
            const yy=y+.25-j*.135,uu=vu+Math.sin(j*.9+vine)*.052,next:Point=[uu,yy,vv];
            member(rail,previous,next,.005);previous=next;
            add(leaf,leaves[(j+vine)%3]!,[.24,.25,.24],point(uu,yy,vv),rotation.multiply(Quaternion.RotationYawPitchRoll(j%2?.7:-.7,0,Math.PI*.65)));
          }
        }
      }
    }
    for(let floor=0;floor<floorCount;floor++){
      const base=groundHeight+floor*floorHeight,v=frontSetback(floor);
      const centres=kind===0?[0]:kind===3?[width*.13]:[-width*.24,width*.24];
      const ww=kind===0?Math.min(1.75,width*.57):kind===3?Math.min(1.50,width*.46):Math.min(1.04,width*.29);
      const bottom=.54,wh=kind===1?1.93:kind===2?1.50:1.79,top=bottom+wh;
      place(wall,[width-.19,bottom,.23],0,base+bottom/2,v-.11);
      place(wall,[width-.19,floorHeight-top,.23],0,base+top+(floorHeight-top)/2,v-.11);
      let left=-width/2+.095;
      for(const u of centres){
        const openingLeft=u-ww/2;
        if(openingLeft>left)place(wall,[openingLeft-left,wh,.23],(left+openingLeft)/2,base+bottom+wh/2,v-.11);
        upperWindow(u,base+bottom,ww,wh,v,kind===0?floor%2===0:kind===3||index%3!==0,kind===1);
        left=u+ww/2;
      }
      if(left<width/2-.095)place(wall,[width/2-.095-left,wh,.23],(left+width/2-.095)/2,base+bottom+wh/2,v-.11);
      place(trim,[width+.08,.125,3.13],0,base-.035,-1.43);
      if(kind===0)balcony(0,base+.06,width*.87,.66,0,v);
      else if(kind===1){
        for(const u of centres)if(floor===0||u<0)balcony(u,base+.09,1.39,.46+(floor%2)*.13,1,v);
        for(const u of [-width/2+.18,width/2-.18])place(paleTrim,[.15,floorHeight,.20],u,base+floorHeight/2,v+.02);
      }else if(kind===2&&floor===0)balcony(-width*.20,base+.06,width*.55,.72,2,v);
      else if(kind===3&&floor===0)balcony(width*.10,base+.08,width*.74,.75,1,v);
      if(kind===2){
        const acU=width*.31;
        place(paleTrim,[.66,.43,.33],acU,base+.64,v+.25);
        for(let j=0;j<6;j++)place(rail,[.52,.016,.027],acU,base+.48+j*.054,v+.424);
      }
    }
    // Broad localized staining follows water paths and surface contacts, with clean bays
    // left between the authored worn areas. Texture opacity carries the irregular edges.
    if((index+kind)%4===1||garage){
      const pu=kind===3?-width*.46:width*.46;
      add(signPlane,wallRunoff,[.31,1.19,1],point(pu,.76,.180),rotation);
      const foot=point(pu,0,.48);contactMark(foot[0],foot[2],.74,.48,.165,index*.31);
    }
    if(kind===1&&index%2===0)add(signPlane,wallRunoff,[width*.40,.72,1],point(-width*.20,.59,.074),rotation);
    if(kind===3)add(signPlane,wallRunoff,[width*.28,.38,1],point(width*.31,.385,.604),rotation);
    if(kind===0&&index%3===0)add(signPlane,wallRunoff,[width*.55,.50,1],point(-width*.12,groundHeight+.24,.009),rotation);
    // Roof silhouettes belong to each shop family, rather than a repeated parapet band.
    if(kind===0){
      place(roofTile,[width+.51,.095,3.76],0,height+.20,-1.00,Quaternion.RotationYawPitchRoll(0,.15,0));
      for(let j=0;j<Math.ceil(width/.20)+2;j++){
        const u=-width/2-.12+j*.20;
        member(roofTile,[u,height+.48,-2.71],[u,height-.04,.74],.040);
      }
      for(let j=0;j<7;j++)place(roofTile,[width+.52,.027,.026],0,height+.44-j*.075,-2.49+j*.49);
      place(wood,[width+.47,.14,.15],0,height-.04,.77);
    }else if(kind===1){
      place(paleTrim,[width+.18,.24,3.25],0,height,-1.42);
      place(wall,[width-.04,.55,.16],0,height+.27,-.01);
      place(paleTrim,[width+.08,.10,.28],0,height+.58,.012);
      for(let j=0;j<5;j++)place(paleTrim,[.12,.59,.21],-width*.43+j*width*.215,height+.28,.02);
    }else if(kind===2){
      place(trim,[width+.16,.16,3.16],0,height,-1.43);
      place(wall,[width,.43,.18],0,height+.19,-.03);
      round(steel,[.83,1.11,.83],-.57,height+.64,-.95);
      round(rail,[.88,.06,.88],-.57,height+1.18,-.95);
      member(steel,[-.57,height+.11,-.95],[-.57,height-.33,-.95],.041);
    }else{
      place(paleTrim,[width+.22,.18,3.17],0,height,-1.42);
      place(wall,[width-.06,.47,.19],0,height+.20,-.03);
      place(roofTile,[width+.14,.11,.36],0,height+.47,.01);
    }
    // Architectural services vary by family and position, with no repeated wall-wide bundle.
    const pipeU=kind===3?-width*.46:width*.46;
    member(steel,[pipeU,.35,.075],[pipeU,height-.05,.075],.029);
    if(kind!==2){
      const acY=kind===1?groundHeight+floorHeight+.55:groundHeight+1.29,acU=kind===0?width*.35:-width*.35;
      place(paleTrim,[.65,.43,.33],acU,acY,.19);
      for(let j=0;j<6;j++)place(rail,[.52,.016,.025],acU,acY-.15+j*.052,.37);
    }
    for(const off of [0,.065])member(soil,[-width/2,groundHeight+2.38+off,.27],[width/2,groundHeight+2.21+off,.27],.009);
    if(kind===0||kind===2)for(let j=0;j<11;j++)place(brick,[.23,.078,.027],width/2-.35-(j%4)*.24,.51+Math.floor(j/4)*.09,.126);
    if(!garage&&(kind===0||kind===1)){
      const boardU=kind===0?-width*.29:width*.34,v=.52,lean=Quaternion.RotationYawPitchRoll(0,-.12,0),which=kind===0?0:1;
      place(wood,[.61,.91,.047],boardU,.66,v,lean);
      add(signPlane,boardSign(which),[.56,.82,1],point(boardU,.67,v+.044),rotation.multiply(lean));
      for(const du of [-.245,.245])member(wood,[boardU+du,.17,v-.24],[boardU+du,1.13,v-.045],.018);
    }
    if(kind===3&&index%2===0){
      const bikeU=-width*.05,bikeV=.46,wheelY=.46;
      for(const offset of [-.62,.62])add(bicycleWheel,soil,[1,1,1],point(bikeU+offset,wheelY,bikeV),rotation.multiply(Quaternion.RotationYawPitchRoll(0,Math.PI/2,0)));
      const rear:Point=[bikeU-.62,wheelY,bikeV],front:Point=[bikeU+.62,wheelY,bikeV],crank:Point=[bikeU-.04,.44,bikeV],seat:Point=[bikeU-.22,.94,bikeV],head:Point=[bikeU+.38,.97,bikeV];
      for(const [a,b]of [[rear,seat],[seat,crank],[crank,rear],[seat,head],[head,crank],[head,front]] as [Point,Point][])member(rail,a,b,.017);
      place(soil,[.23,.043,.11],bikeU-.25,1.08,bikeV);
      member(steel,[bikeU-.22,.94,bikeV],[bikeU-.22,1.07,bikeV],.013);
      member(steel,[bikeU+.38,.97,bikeV],[bikeU+.34,1.14,bikeV],.014);
      member(steel,[bikeU+.34,1.14,bikeV-.20],[bikeU+.34,1.14,bikeV+.20],.013);
    }
  }

  // Individual curb stones and paver seams read from the chase camera's low angle.
  for(const side of [-1,1]){
    box(pavement,[1.22,.14,length],[side*(edge+.63),.066,startZ+length/2]);
    const rows=Math.ceil(length/.46);
    for(let row=0;row<rows;row++){
      const z=startZ+(row+.5)*.46;if(z>startZ+length)continue;
      box(curb,[.23,.20,.444],[side*(edge+.115),.082,z]);
      for(let col=0;col<3;col++)box(paverTints[Math.abs((row*127+col*31)*(row+7)+side*47)%4]!,[.285,.015+(row%3)*.003,.423],[side*(edge+.385+col*.292),.147,z+(col%2)*.014]);
      if(row%14===4){
        box(soil,[.29,.006,.33],[side*(edge+.38),.166,z]);
        for(let j=0;j<6;j++)box(steel,[.27,.011,.015],[side*(edge+.38),.172,z-.13+j*.05]);
      }
    }
    const leftLayout:readonly (readonly [0|1|2|3,number,number])[]=[
      [3,3.1,.18],[2,3.8,.46],[0,4.6,0],[1,4.2,.27],[3,2.9,.58],
      [0,4.8,.09],[2,3.45,.38],[3,3.0,.17],[1,4.4,.43],[0,4.1,.03],
      [3,3.25,.28],[2,3.55,.16],[1,4.0,.50],[0,4.6,0],[3,3.0,.24],
    ];
    const rightLayout:readonly (readonly [0|1|2|3,number,number])[]=[
      [1,4.3,.38],[3,3.25,.13],[0,4.2,.27],[2,3.5,.03],[3,3.0,.42],
      [1,4.75,.09],[0,4.1,.52],[3,2.9,.16],[2,3.65,.35],[0,4.4,0],
      [1,4.2,.36],[3,3.35,.15],[2,3.45,.40],[0,4.5,.05],[1,4.1,.23],
    ];
    const layout=side<0?leftLayout:rightLayout;
    let z=startZ+.05,index=0;
    while(z<startZ+length-.2){
      const bay=layout[index%layout.length]!,width=Math.min(bay[1],startZ+length-z);
      if(width<2.4)break;
      house(side,z+width/2,width,index+(side>0?19:0),bay[0],bay[2]);z+=width;index++;
    }
  }
  // Utility drops and sagging cable bundles connect both building rows.
  for(let i=0;i<6;i++){
    const z=startZ+7+i*9.2;
    for(let cable=0;cable<3;cable++){
      const y=6.4+(i%3)*.37+cable*.08;
      for(let segment=0;segment<14;segment++){
        const curve=(t:number):Point=>[-facade+t*facade*2,y-.67*Math.sin(t*Math.PI),z+cable*.07+.14*Math.sin(t*Math.PI)];
        rod(soil,curve(segment/14),curve((segment+1)/14),.008);
      }
    }
    if(i%2===0)for(let j=0;j<4;j++){
      const x=-3+j*1.47,y=6.19-.57*Math.sin((j+1)/6*Math.PI);
      const flag=new VertexData();
      flag.positions=[0,0,0,.60,-.04,.065,.54,-.83,.13,-.045,-.74,.045];
      flag.indices=[0,1,2,0,2,3];flag.uvs=[0,0,1,0,1,1,0,1];
      const normals:number[]=[];VertexData.ComputeNormals(flag.positions,flag.indices,normals);flag.normals=normals;
      add(flag,flagRed,[1,1,1],[x,y,z+.07]);
      const star=new VertexData();star.positions=[.277,-.39,.024];star.uvs=[.5,.5];star.indices=[];
      for(let k=0;k<10;k++){
        const a=k*Math.PI/5-Math.PI/2,r=k%2===0?.151:.061;
        star.positions.push(.277+Math.cos(a)*r,-.39+Math.sin(a)*r,.024);star.uvs.push(.5+Math.cos(a)*.5,.5+Math.sin(a)*.5);star.indices.push(0,k+1,(k+1)%10+1);
      }
      const starNormals:number[]=[];VertexData.ComputeNormals(star.positions,star.indices,starNormals);star.normals=starNormals;
      add(star,flagYellow,[1,1,1],[x,y,z+.07]);
    }
  }
  // The playable route ends before these staggered background blocks. A narrow
  // continuing view remains between them instead of a wall across the street.
  const farZ=startZ+length+26;
  const distantBlocks=[[-7.1,10.1,-7],[-4.7,8.1,4],[5.1,11.3,8],[9.1,8.7,-1],[-3.9,7.2,31],[5.2,9.2,41],[.7,6.5,58],[-2.4,8.3,68]] as const;
  for(let bay=0;bay<distantBlocks.length;bay++){
    const block=distantBlocks[bay]!,x=block[0],height=block[1],z=farZ+block[2];
    const wall=plaster[(bay+2)%plaster.length]!;
    box(wall,[4.1,height,4],[x,height/2,z]);
    box(paleTrim,[4.23,.18,4.1],[x,height,z]);
    box(trim,[4.13,.13,4.1],[x,3.05,z]);
    for(let floor=0;floor<3;floor++)for(const u of [-1,1]){
      box(dark,[.85,1.36,.026],[x+u,1.75+floor*2.6,z-2.016]);
      box(shutter,[.22,1.4,.09],[x+u-.44,1.75+floor*2.6,z-2.04]);
      box(shutter,[.22,1.4,.09],[x+u+.44,1.75+floor*2.6,z-2.04]);
      box(trim,[1.24,.105,.27],[x+u,1.06+floor*2.6,z-2.07]);
    }
  }
  for(const side of [-1,1]){
    box(pavement,[1.22,.14,30],[side*(edge+.63),.066,startZ+length+15]);
    box(curb,[.23,.20,30],[side*(edge+.115),.082,startZ+length+15]);
  }
  // Continue the visible asphalt through the distant scenery: the old strip left
  // a 36m pale gap beyond the playable road. This surface has no simulation role.
  const distantRoad:MaterialRef=options.material
    ? {material:options.material('asphalt',[.82,.77,.69],'asphalt'),color:[1,1,1]}
    : material('distant-road',[.32,.30,.27],'asphalt');
  box(distantRoad,[edge*2,.024,112],[0,-.003,startZ+length+56]);
  // Low-detail continuations sit beyond the benchmark so the hero street retains depth.
  for(const side of [-1,1])for(let bay=0;bay<6;bay++){
    const z=startZ+length+2.3+bay*4.5,height=8.4+(bay%3)*.9,x=side*(facade+1.5);
    box(plaster[(bay+2)%plaster.length]!,[3,height,4.43],[x,height/2,z]);
    box(paleTrim,[3.1,.18,4.51],[x,height,z]);
    for(const y of [4.2,7]){
      box(dark,[.035,1.4,1.16],[side*(facade-.027),y,z]);
      box(trim,[.34,.10,1.43],[side*(facade-.13),y-.73,z]);
      box(rail,[.037,.05,3.8],[side*(facade-.57),y-.04,z]);
      for(let j=0;j<9;j++)box(rail,[.029,.57,.029],[side*(facade-.57),y-.30,z-1.78+j*.445]);
    }
  }
  // Two flat asphalt repairs break up the road's broad material field. These are
  // transparent surface marks 4mm above the road, with no collision or steering role.
  add(signPlane,roadRepair,[1.31,2.16,1],[.12,.016,6.7],Quaternion.RotationYawPitchRoll(.24,0,0).multiply(groundWearRotation));
  add(signPlane,roadRepair,[1.69,2.70,1],[-.21,.016,23.4],Quaternion.RotationYawPitchRoll(-.31,0,0).multiply(groundWearRotation));
  const meshes:Mesh[]=[];let triangleCount=0;
  for(const [key,b]of batches){
    const data=new VertexData();data.positions=b.positions;data.normals=b.normals;data.uvs=b.uvs;data.indices=b.indices;data.colors=b.colors;
    const mesh=new Mesh('adventure-static-'+key,scene);data.applyToMesh(mesh);mesh.material=b.material;mesh.parent=root;
    mesh.receiveShadows=true;mesh.isPickable=false;mesh.freezeWorldMatrix();meshes.push(mesh);triangleCount+=b.indices.length/3;
  }
  return{root,meshes,triangleCount,dispose(){root.dispose();for(const mat of ownMaterials)mat.dispose(false,false);for(const texture of ownTextures)texture.dispose();}};
}
